import React from "react";
import { supabase } from "@/integrations/supabase/client";

type App = {
  id: string;
  user_id: string;
  business_name: string;
  business_type: string;
  tax_id: string | null;
  contact_phone: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  website: string | null;
  description: string;
  product_categories: string[];
  expected_monthly_volume: string | null;
  status: "pending" | "approved" | "rejected";
  admin_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
};

export function AdminVendorApplications({ lang, toast }: { lang: "es" | "en"; toast?: (m: string) => void }) {
  const t = (es: string, en: string) => (lang === "es" ? es : en);
  const [rows, setRows] = React.useState<App[]>([]);
  const [emails, setEmails] = React.useState<Record<string, string>>({});
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState<"all" | "pending" | "approved" | "rejected">("pending");
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [notesById, setNotesById] = React.useState<Record<string, string>>({});
  const [commById, setCommById] = React.useState<Record<string, string>>({});

  const load = React.useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("vendor_applications")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    const apps = (data ?? []) as App[];
    setRows(apps);
    const ids = Array.from(new Set(apps.map(a => a.user_id)));
    if (ids.length) {
      const { data: profs } = await supabase.from("profiles")
        .select("user_id,email,first_name,last_name").in("user_id", ids);
      const map: Record<string, string> = {};
      (profs ?? []).forEach((p: any) => {
        map[p.user_id] = [p.first_name, p.last_name].filter(Boolean).join(" ") + (p.email ? ` <${p.email}>` : "");
      });
      setEmails(map);
    }
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const visible = rows.filter(r => filter === "all" || r.status === filter);

  const approve = async (a: App) => {
    setBusyId(a.id);
    const rate = parseFloat(commById[a.id] || "0.15");
    const { error } = await supabase.rpc("approve_vendor_application", {
      _application_id: a.id,
      _commission_rate: isNaN(rate) ? 0.15 : Math.max(0, Math.min(1, rate)),
    });
    setBusyId(null);
    if (error) { toast?.(t("Error: ", "Error: ") + error.message); return; }
    toast?.(t("Vendedor aprobado", "Vendor approved"));
    await load();
  };

  const reject = async (a: App) => {
    setBusyId(a.id);
    const { error } = await supabase.rpc("reject_vendor_application", {
      _application_id: a.id,
      _notes: notesById[a.id] || null,
    });
    setBusyId(null);
    if (error) { toast?.(t("Error: ", "Error: ") + error.message); return; }
    toast?.(t("Solicitud rechazada", "Application rejected"));
    await load();
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: 0 }}>{t("Solicitudes de vendedor", "Vendor applications")}</h3>
          <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>
            {t("Onboarding self-service para nuevos vendedores.", "Self-service onboarding for new vendors.")}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {(["pending", "approved", "rejected", "all"] as const).map(f => (
            <button key={f} className={`btn ${filter === f ? "primary" : ""}`} onClick={() => setFilter(f)}>
              {f === "pending" ? t("Pendientes", "Pending") :
               f === "approved" ? t("Aprobadas", "Approved") :
               f === "rejected" ? t("Rechazadas", "Rejected") :
               t("Todas", "All")}
              <span style={{ marginLeft: 6, opacity: .7 }}>
                ({f === "all" ? rows.length : rows.filter(r => r.status === f).length})
              </span>
            </button>
          ))}
        </div>
      </div>

      {loading ? <p style={{ marginTop: 16 }}>{t("Cargando…", "Loading…")}</p>
        : visible.length === 0 ? <p style={{ color: "#888", marginTop: 16 }}>{t("Sin solicitudes.", "No applications.")}</p>
        : (
          <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
            {visible.map(a => {
              const color = a.status === "approved" ? "#16a34a" : a.status === "rejected" ? "#dc2626" : "#d97706";
              return (
                <div key={a.id} style={{ background: "#fff", border: "1px solid #e5e5e5", borderRadius: 12, padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                    <div>
                      <strong style={{ fontSize: 16 }}>{a.business_name}</strong>
                      <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                        {a.business_type} · {emails[a.user_id] || a.user_id.slice(0, 8) + "…"}
                      </div>
                      <div style={{ fontSize: 12, color: "#888" }}>{new Date(a.created_at).toLocaleString()}</div>
                    </div>
                    <span style={{ background: color, color: "#fff", padding: "4px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                      {a.status}
                    </span>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px,1fr))", gap: 8, marginTop: 12, fontSize: 13 }}>
                    {a.tax_id && <Detail label={t("RNC", "Tax ID")} value={a.tax_id} />}
                    {a.contact_phone && <Detail label={t("Tel", "Phone")} value={a.contact_phone} />}
                    {a.website && <Detail label="Web" value={a.website} />}
                    {(a.city || a.country) && <Detail label={t("Ubicación", "Location")} value={[a.city, a.country].filter(Boolean).join(", ")} />}
                    {a.expected_monthly_volume && <Detail label={t("Volumen", "Volume")} value={a.expected_monthly_volume} />}
                  </div>

                  <div style={{ marginTop: 10, fontSize: 13 }}>
                    <strong>{t("Categorías", "Categories")}: </strong>
                    {a.product_categories.join(", ")}
                  </div>

                  <p style={{ marginTop: 8, fontSize: 14, whiteSpace: "pre-wrap", background: "#fafafa", padding: 10, borderRadius: 8 }}>
                    {a.description}
                  </p>

                  {a.admin_notes && (
                    <p style={{ marginTop: 8, fontSize: 13, color: "#666" }}>
                      <strong>{t("Notas", "Notes")}:</strong> {a.admin_notes}
                    </p>
                  )}

                  {a.status === "pending" && (
                    <div style={{ marginTop: 12, borderTop: "1px solid #eee", paddingTop: 12, display: "grid", gap: 10 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <label style={{ fontSize: 12, fontWeight: 600 }}>{t("Comisión", "Commission")}</label>
                        <input
                          type="number" step="0.01" min="0" max="1"
                          placeholder="0.15"
                          style={{ width: 90, padding: "6px 8px", border: "1px solid #ddd", borderRadius: 6 }}
                          value={commById[a.id] ?? ""}
                          onChange={e => setCommById(p => ({ ...p, [a.id]: e.target.value }))}
                        />
                        <span style={{ fontSize: 12, color: "#888" }}>{t("(0–1, por defecto 0.15)", "(0–1, default 0.15)")}</span>
                      </div>
                      <textarea
                        rows={2} placeholder={t("Notas para el solicitante (opcional)", "Notes for applicant (optional)")}
                        style={{ padding: 8, border: "1px solid #ddd", borderRadius: 6, fontSize: 13 }}
                        value={notesById[a.id] ?? ""}
                        onChange={e => setNotesById(p => ({ ...p, [a.id]: e.target.value }))}
                      />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="btn primary" disabled={busyId === a.id} onClick={() => approve(a)}>
                          {busyId === a.id ? "…" : t("✓ Aprobar y conceder rol", "✓ Approve & grant role")}
                        </button>
                        <button className="btn" disabled={busyId === a.id} onClick={() => reject(a)} style={{ color: "#dc2626" }}>
                          {t("✗ Rechazar", "✗ Reject")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase" }}>{label}</div>
      <div>{value}</div>
    </div>
  );
}